import React from 'react';
import { Box, Typography, Chip, Paper } from '@mui/material';
import LockIcon from '@mui/icons-material/Lock';
import InventoryIcon from '@mui/icons-material/Inventory2';
import { getTaskType, taskTypeIcon } from '../../utils/taskTypes';
import { highlight, formatDateTime } from './highlight';

/**
 * Eine Trefferzeile: Ticket-Kopf plus die passenden Kommentarstellen.
 *
 * Geteilt zwischen dem Panel unter dem Board und der globalen Suche, damit ein
 * Treffer überall gleich aussieht. Die Kommentarstellen sind der eigentliche
 * Punkt bei Abnahme-/Streitfall-Recherche: Autor und Datum stehen deshalb
 * direkt an jedem Snippet.
 */
function SearchResultItem({ result, terms, onOpen, showProject = false }) {
  const { task } = result;
  const type = getTaskType(task.task_type);
  const hiddenComments = result.comment_match_count - (result.comment_matches || []).length;

  return (
    <Paper
      variant="outlined"
      onClick={() => onOpen(result)}
      sx={{
        p: 1.5,
        cursor: 'pointer',
        transition: 'border-color 120ms, background-color 120ms',
        '&:hover': { borderColor: 'primary.main', bgcolor: 'action.hover' },
      }}
    >
      {/* Ticket-Kopf */}
      <Box sx={{ display: 'flex', alignItems: 'flex-start', gap: 1, flexWrap: 'wrap' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, color: 'text.secondary' }}>
          <Box component="span" title={type.label} sx={{ display: 'inline-flex', color: type.color }}>
            {taskTypeIcon(task.task_type, { sx: { fontSize: 18 } })}
          </Box>
          <Typography variant="caption" sx={{ fontFamily: 'monospace' }}>
            #{task.id}
          </Typography>
        </Box>

        <Typography variant="body2" sx={{ fontWeight: 600, flex: 1, minWidth: 200 }}>
          {highlight(task.name, terms)}
        </Typography>

        {result.status_name && (
          <Chip
            size="small"
            variant="outlined"
            color={result.is_done_status ? 'success' : 'default'}
            label={result.status_name}
          />
        )}
        {!result.is_active && (
          <Chip size="small" color="default" icon={<InventoryIcon />} label="Archiviert" />
        )}
      </Box>

      {(showProject || result.customer_name) && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 0.5 }}>
          {[result.customer_name, showProject ? result.project_name : null]
            .filter(Boolean)
            .join(' · ')}
        </Typography>
      )}

      {/* Kommentarstellen – wer hat wann was gesagt */}
      {(result.comment_matches || []).length > 0 && (
        <Box sx={{ mt: 1, display: 'flex', flexDirection: 'column', gap: 0.75 }}>
          {result.comment_matches.map((c) => (
            <Box
              key={c.comment_id}
              sx={{
                borderLeft: 2,
                borderColor: 'divider',
                pl: 1.25,
                ...(c.is_internal && { bgcolor: 'rgba(255, 193, 7, 0.08)', borderRadius: 0.5 }),
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, flexWrap: 'wrap' }}>
                <Typography variant="caption" sx={{ fontWeight: 600 }}>
                  {c.author || 'Unbekannt'}
                </Typography>
                {c.author_role === 'client' && (
                  <Chip size="small" variant="outlined" color="info" label="Kunde"
                        sx={{ height: 18, '& .MuiChip-label': { px: 0.75, fontSize: '0.65rem' } }} />
                )}
                {c.is_internal && (
                  <Chip size="small" variant="outlined" color="warning" icon={<LockIcon />} label="intern"
                        sx={{ height: 18, '& .MuiChip-label': { px: 0.5, fontSize: '0.65rem' } }} />
                )}
                <Typography variant="caption" color="text.secondary">
                  {formatDateTime(c.created_at)}
                </Typography>
              </Box>
              <Typography variant="caption" color="text.secondary" sx={{ display: 'block' }}>
                {highlight(c.snippet, terms)}
              </Typography>
            </Box>
          ))}

          {hiddenComments > 0 && (
            <Typography variant="caption" color="text.secondary" sx={{ pl: 1.25 }}>
              + {hiddenComments} weitere Fundstelle{hiddenComments === 1 ? '' : 'n'} in diesem Ticket
            </Typography>
          )}
        </Box>
      )}
    </Paper>
  );
}

export default SearchResultItem;
